import { decodeJwtPayload } from "../helpers/shared-session.js";
import { createReportHostBridge } from "./report-host-bridge.js";

/** A refresh that has not answered by then is given up on and answered `network`. */
const REPORT_REFRESH_TIMEOUT_MS = 30000;

/**
 * The host bridge of the custom reports page's frame, wired to the page's own
 * session (see `report-host-bridge.js` for the protocol). The page holds the
 * login in `localStorage` and axios's default Authorization header; neither is
 * ever posted into the frame.
 *
 * @param {object} options
 * @param {() => ({ postMessage: Function } | null)} options.getFrameWindow the report frame's window, or null
 * @param {string} options.apiHost base URL of the public API (`publicApiHost`)
 * @param {string} options.dashboardHost the dashboard's own URL, for reports of the old protocol
 * @param {{ getItem: (key: string) => string | null }} options.storage `localStorage`
 * @param {(url: string, config: object) => Promise<{ status: number, headers?: object, data?: unknown }>} options.httpGet
 *   `axios.get`, which carries the session
 */
export function createReportFrameBridge({
  getFrameWindow,
  apiHost,
  dashboardHost,
  storage,
  httpGet,
}) {
  /** Who is signed in and which org is selected, or null when nobody is. */
  function identity() {
    const payload = decodeJwtPayload(storage.getItem("jwt_token") || "");
    const loginId = Number(payload?.id);
    const orgId = Number(storage.getItem("orgId"));
    if (!Number.isInteger(loginId) || loginId <= 0) return null;
    if (!Number.isInteger(orgId) || orgId <= 0) return null;
    // This dashboard keeps no profile in the store; the bridge needs only an answer.
    return {
      user: { login_id: loginId, first_name: "", last_name: "", email: "" },
      org_id: orgId,
    };
  }

  /**
   * GET with the page's session. Any HTTP answer is passed on, error statuses
   * included (so a report's 401 shows no dashboard toast and signs nobody out),
   * as status, content type and body only; a request with no answer rejects.
   * No retry: the report decides whether to try again.
   */
  async function fetchWithDashboardSession(url, signal) {
    const response = await httpGet(url, {
      responseType: "text",
      transformResponse: [(d) => d],
      signal,
      timeout: REPORT_REFRESH_TIMEOUT_MS,
      validateStatus: () => true,
    });
    const data = response.data;
    let body = "";
    if (typeof data === "string") body = data;
    else if (data !== undefined && data !== null) body = JSON.stringify(data);
    return {
      status: response.status,
      contentType: String(response.headers?.["content-type"] || ""),
      body,
    };
  }

  return createReportHostBridge({
    getFrameWindow,
    apiBaseUrl: apiHost,
    getIdentity: identity,
    fetcher: fetchWithDashboardSession,
    getLegacyAuthContext: () => ({
      orgId: storage.getItem("orgId") || "",
      apiHost,
      dashboardHost,
    }),
  });
}
